import apiClient from './client';

// Types for industry detection
export interface IndustryProfile {
  industry: string;
  industryDisplayName: string;
  businessType: string;
  businessName: string;
  // DYN-96. Null whenever no number was produced: the keyword scan that runs
  // when the model is unavailable, the list the user picks from themselves, and
  // a model that reported no confidence of its own. `method` names the producer,
  // because the null alone cannot tell those three apart.
  confidence: number | null;
  method: 'model' | 'keywords' | 'manual';
  products: string[];
  targetMarket: 'B2B' | 'B2C' | 'both';
  suggestedIntegrations: IntegrationSuggestion[];
  websiteUrl: string;
  analyzedAt: string;
  kpis: {
    primary: string[];
    secondary: string[];
  };
  terminology: Record<string, string>;
  industryConfig: {
    icon: string;
    color: string;
  };
}

export interface IntegrationSuggestion {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  /**
   * Whether this integration can actually be connected today. The backend
   * derives it from OAUTH_PROVIDERS; everything else 501s on connect (DYN-19).
   * Optional because the local fallback list below predates the field.
   */
  available?: boolean;
}

export interface DashboardConfig {
  industry: string;
  industryDisplayName: string;
  kpis: {
    primary: string[];
    secondary: string[];
  };
  kpiDefinitions: Record<string, {
    label: string;
    format: string;
    icon: string;
  }>;
  terminology: Record<string, string>;
  suggestedIntegrations: IntegrationSuggestion[];
  icon: string;
  color: string;
}

export interface IndustryOption {
  value: string;
  label: string;
}

// Analyze a website to detect industry
export const analyzeWebsite = async (url: string): Promise<IndustryProfile> => {
  const response = await apiClient.post('/onboarding/analyze-website', { url });
  return response.data;
};

// Save industry profile after analysis
export const saveIndustryProfile = async (data: {
  industry: string;
  businessType?: string;
  businessName?: string;
  websiteUrl?: string;
  customKpis?: string[];
}): Promise<{ message: string; industry: string; industryDisplayName: string }> => {
  const response = await apiClient.post('/onboarding/save-industry-profile', data);
  return response.data;
};

// Get list of all available industries
export const getIndustries = async (): Promise<{ industries: IndustryOption[] }> => {
  const response = await apiClient.get('/onboarding/industries');
  return response.data;
};

// Get configuration for a specific industry
export const getIndustryConfig = async (industry: string): Promise<DashboardConfig> => {
  const response = await apiClient.get(`/onboarding/industry-config/${industry}`);
  return response.data;
};

// Get the current user's dashboard configuration
export const getDashboardConfig = async (): Promise<DashboardConfig> => {
  const response = await apiClient.get('/onboarding/dashboard-config');
  return response.data;
};
